type DeployVersionPayload = {
  app?: string
  buildId?: string
  builtAt?: string
}

export type DeployVersionGuardOptions = {
  /** Matches deployVersionPlugin app name */
  appName: string
  /** Poll interval in ms (default 3 min) */
  pollIntervalMs?: number
  /** Auto reload after showing prompt (default 45s). Set 0 to require manual confirm. */
  autoReloadMs?: number
}

const VERSION_URL = '/version.json'
let guardStarted = false
let reloadScheduled = false

function getEmbeddedBuildId(): string {
  return (import.meta.env.VITE_APP_BUILD_ID as string | undefined)?.trim() || 'dev'
}

function hardReload(): void {
  if (reloadScheduled) return
  reloadScheduled = true
  window.location.reload()
}

async function fetchRemoteBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) return null
    const data = (await res.json()) as DeployVersionPayload
    return data.buildId?.trim() || null
  } catch {
    return null
  }
}

function promptReload(reason: string, autoReloadMs: number): Promise<void> {
  if (reloadScheduled) return Promise.resolve()

  return new Promise((resolve) => {
    let countdownTimer: number | null = null

    const cleanup = () => {
      if (countdownTimer !== null) {
        window.clearInterval(countdownTimer)
        countdownTimer = null
      }
    }

    const overlay = document.createElement('div')
    overlay.setAttribute('data-deploy-version-guard', 'true')
    overlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:2147483000',
      'background:rgba(2,6,23,0.82)',
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'padding:16px',
    ].join(';')

    const card = document.createElement('div')
    card.style.cssText = [
      'width:min(420px,100%)',
      'background:#fff',
      'color:#111827',
      'border-radius:16px',
      'padding:20px 22px',
      'box-shadow:0 20px 60px rgba(0,0,0,0.35)',
      'font-family:system-ui,-apple-system,sans-serif',
    ].join(';')

    const title = document.createElement('div')
    title.textContent = 'มีเวอร์ชันใหม่'
    title.style.cssText = 'font-size:20px;font-weight:700;margin-bottom:8px;'

    const body = document.createElement('div')
    body.textContent = reason
    body.style.cssText = 'font-size:14px;line-height:1.5;color:#4b5563;margin-bottom:16px;'

    const hint = document.createElement('div')
    hint.style.cssText = 'font-size:13px;color:#6b7280;margin-bottom:16px;'

    const updateCountdownHint = (remainingSec: number) => {
      if (remainingSec <= 0) {
        hint.textContent = 'กำลังรีเฟรช...'
        return
      }
      hint.innerHTML = `ระบบจะรีเฟรชอัตโนมัติใน <strong style="color:#2563eb;font-size:15px;">${remainingSec}</strong> วินาที`
    }

    if (autoReloadMs > 0) {
      updateCountdownHint(Math.ceil(autoReloadMs / 1000))
    } else {
      hint.textContent = 'กรุณารีเฟรชเพื่อใช้งานเวอร์ชันล่าสุด'
    }

    const actions = document.createElement('div')
    actions.style.cssText = 'display:flex;gap:10px;justify-content:flex-end;'

    const reload = () => {
      cleanup()
      overlay.remove()
      hardReload()
      resolve()
    }

    if (autoReloadMs <= 0) {
      const laterBtn = document.createElement('button')
      laterBtn.type = 'button'
      laterBtn.textContent = 'ภายหลัง'
      laterBtn.style.cssText = 'padding:10px 14px;border:1px solid #d1d5db;border-radius:10px;background:#fff;cursor:pointer;'
      laterBtn.onclick = () => {
        cleanup()
        overlay.remove()
        resolve()
      }
      actions.appendChild(laterBtn)
    }

    const nowBtn = document.createElement('button')
    nowBtn.type = 'button'
    nowBtn.textContent = 'อัปเดตเลย'
    nowBtn.style.cssText = 'padding:10px 14px;border:0;border-radius:10px;background:#2563eb;color:#fff;font-weight:600;cursor:pointer;'
    nowBtn.onclick = reload
    actions.appendChild(nowBtn)

    card.append(title, body, hint, actions)
    overlay.appendChild(card)
    document.body.appendChild(overlay)

    if (autoReloadMs > 0) {
      let remainingSec = Math.ceil(autoReloadMs / 1000)
      countdownTimer = window.setInterval(() => {
        remainingSec -= 1
        if (remainingSec <= 0) {
          reload()
          return
        }
        updateCountdownHint(remainingSec)
      }, 1000)
    }
  })
}

async function checkForNewDeploy(appName: string, autoReloadMs: number): Promise<void> {
  const embedded = getEmbeddedBuildId()
  if (!embedded || embedded === 'dev') return

  const remote = await fetchRemoteBuildId()
  if (!remote || remote === embedded) return

  await promptReload(
    `ระบบมีการอัปเดต (${appName}) — เวอร์ชันปัจจุบันของคุณอาจล้าสมัย`,
    autoReloadMs
  )
}

/**
 * Poll /version.json and reload when a new frontend build is deployed.
 * Also reloads on Vite chunk preload failures (stale lazy-loaded assets).
 */
export function startDeployVersionGuard(options: DeployVersionGuardOptions): void {
  if (guardStarted || typeof window === 'undefined') return
  guardStarted = true

  const pollIntervalMs = options.pollIntervalMs ?? 3 * 60 * 1000
  const autoReloadMs = options.autoReloadMs ?? 45_000

  const runCheck = () => {
    void checkForNewDeploy(options.appName, autoReloadMs)
  }

  window.setTimeout(runCheck, 15_000)
  window.setInterval(runCheck, pollIntervalMs)

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      runCheck()
    }
  })

  window.addEventListener('focus', runCheck)

  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault()
    void promptReload(
      'ไม่สามารถโหลดไฟล์ระบบบางส่วนได้ (อาจเกิดจากการอัปเดตเวอร์ชัน)',
      10_000
    )
  })
}
